import { IconDots, IconPalette } from '@tabler/icons-react';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Card, Grid, Title } from '@mantine/core';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import StripoGallery from '../../components/Stripo/StripoGallery';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';

const StripoTemplatesPage = ({ close }) => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const filterDefaultValues = useRef({
    type: 'PREMIUM',
    templateTypes: [],
    templateSeasons: [],
    templateFeatures: [],
    templateIndustries: [],
  });

  const [templates, setTemplates] = useState([]);
  const [filterOptions, setFilterOptions] = useState();
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const { accountId, domainId } = useParams();
  const currentFilters = queryString.parse(location.search, { arrayFormat: 'bracket' });

  const newFilters = useMemo(() => {
    return {
      ...filterDefaultValues.current,
      ...queryString.parse(location.search, { arrayFormat: 'bracket' }),
      page,
    };
  }, [location.search, page]);

  const fetchStripoTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { templates, total } = await fetch(
        `/api/admin/accounts/${accountId}/domains/${domainId}/stripo/templates?${queryString.stringify(newFilters, {
          arrayFormat: 'bracket',
        })}`
      );

      setTemplates((prevTemplates) => [...prevTemplates, ...templates]);
      setTotalCount(total);
      setLoading(false);
    } catch (error) {
      setError(error);
    }
  }, [fetch, accountId, domainId, newFilters]);

  useEffect(() => {
    fetchStripoTemplates();
  }, [fetchStripoTemplates, page]);

  useEffect(() => {
    setFilterOptions(undefined);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/stripo/templates/filterOptions`)
      .then((options) => {
        setFilterOptions(options);
      })
      .catch(setError);
  }, [accountId, domainId, fetch]);

  const loadMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const handleFilter = (name, value) => {
    const newFilters = {
      ...currentFilters,
      [name]: value,
    };

    const newSearch = queryString.stringify(newFilters, { arrayFormat: 'bracket' });
    navigate({ pathname: location.pathname, search: `?${newSearch}` });
    setTemplates([]);
    setPage(0);
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  return (
    <>
      <Metadata title={`${Page['stripoTemplates']}`} />

      <Card mb="sm" withBorder>
        {!loading ? (
          <Box style={{ float: 'left' }}>
            <Title order={5}>
              <IconPalette size={16} /> {totalCount?.toLocaleString(navigator.language)} Email Templates
            </Title>
          </Box>
        ) : (
          'Loading...'
        )}
      </Card>

      <StripoGallery
        templates={templates}
        filterOptions={filterOptions}
        currentFilters={currentFilters}
        onFilterChange={handleFilter}
        close={close}
      />

      {loading ? (
        <Spinner />
      ) : (
        <Grid>
          <Grid.Col span={12}>
            <Button size="lg" fullWidth onClick={loadMore} disabled={loading || totalCount === templates.length}>
              <IconDots size={14} /> Load More
            </Button>
          </Grid.Col>
        </Grid>
      )}
    </>
  );
};

export default StripoTemplatesPage;
